import { ChangeDetectionStrategy, Component, OnInit, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TableModule } from 'primeng/table';
import { PanelModule } from 'primeng/panel';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { SkeletonModule } from 'primeng/skeleton';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { Select } from 'primeng/select';
import { FormsModule } from '@angular/forms';
import { ContractService } from '../contract/contract.service';
import { ExternalContract } from './model/external-contract';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CustomerService } from '../../common/service/customer.service';
import { Advisor } from '../home/advisor/model/advisor';
import { UserService } from '../../common/service/user.service';
import { ContractStatus } from '../contract/enum/contract-status.enum';
import { ContractStatusPipe } from '../contract/pipes/contract-status.pipe';
import { EnumConverter } from '../../common/helper/enum-converter';
import { TranslationService } from '../../common/service/translate.service';
import { TablePersistenceDirective } from '../../common/directive/table-persistence.directive';
import {
  clearTableSessionState,
  getTableSearchValue,
  setTableSearchValue,
  TablePersistenceConfig,
} from '../../common/helper/table-session-state';
import { Table } from 'primeng/table';
import { TableFilterIconComponent } from '../../common/component/table-filter-icon/table-filter-icon.component';
import { RouterLink } from '@angular/router';

@Component({
  selector: 'gate-external-contract',
  standalone: true,
  imports: [
    CommonModule,
    TableModule,
    PanelModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    SkeletonModule,
    ButtonModule,
    TagModule,
    Select,
    FormsModule,
    TranslateModule,
    ContractStatusPipe,
    TableFilterIconComponent,
    TablePersistenceDirective,
    RouterLink,
  ],
  templateUrl: './external-contract.component.html',
  styleUrl: './external-contract.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExternalContractComponent implements OnInit {
  private contractService = inject(ContractService);
  private customerService = inject(CustomerService);
  private userService = inject(UserService);
  private translate = inject(TranslateService);
  private translationService = inject(TranslationService);
  public readonly tableStateKey = 'externalContractTableState';

  public externalContracts = signal<ExternalContract[]>([]);
  public contractStatuses: any[] = [];
  public isLoading = signal<boolean>(true);
  public location = signal<string | undefined>('');
  public advisor = signal<Advisor | undefined>(undefined);
  public searchValue = signal<string>('');

  public clearAllFilter(table: Table) {
    clearTableSessionState(this.tableStateKey);
    this.searchValue.set('');
    table.clear();
  }

  public onGlobalSearch(value: string, table: Table) {
    this.searchValue.set(value);
    setTableSearchValue(this.tableStateKey, value);
    table.filterGlobal(value, 'contains');
  }

  ngOnInit(): void {
    this.mapContractStatusEnumToArray();
    this.restoreSearchValue();
    this.loadExternalContracts();
    this.getCustomerAddress();
    this.getAdvisorDetail();
    this.translate.onLangChange.subscribe(() => this.mapContractStatusEnumToArray());
  }

  private restoreSearchValue(): void {
    const savedSearch = getTableSearchValue(this.tableStateKey);
    if (savedSearch) {
      this.searchValue.set(savedSearch);
    }
  }

  private mapContractStatusEnumToArray(): void {
    const contractStatuses = EnumConverter.ConvertEnumToArray(ContractStatus);
    const statusPipe = new ContractStatusPipe();

    contractStatuses.forEach((element) => {
      element.key = this.translationService.translateKey(statusPipe.transform(element.value));
    });

    this.contractStatuses = contractStatuses;
  }

  public getSeverity(status: ContractStatus): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' | undefined {
    if (status == ContractStatus.active) {
      return 'success';
    }

    if (status == ContractStatus.expired) {
      return 'danger';
    }

    return undefined;
  }

  public isEndingWithin3Months(endDate: string | Date | undefined): boolean {
    if (!endDate) {
      return false;
    }
    const end = new Date(endDate);
    const now = new Date();
    const threeMonthsFromNow = new Date();
    threeMonthsFromNow.setMonth(now.getMonth() + 3);

    return end >= now && end <= threeMonthsFromNow;
  }

  public parseOrderNumbers(orderString: string | undefined): string[] {
    if (!orderString || !orderString.trim()) {
      return [];
    }
    // Splits by spaces, +, -, comma, or newlines while keeping order tokens clean
    return orderString
      .split(/[\s+,\n\r]+|(?<=\S)\s*[-+]\s*(?=\S)/)
      .map((item) => item.trim().replace(/^[-+]+|[-+]+$/g, ''))
      .filter((item) => item.length > 0);
  }

  private getCustomerAddress() {
    this.customerService.getLocation().then((res) => {
      this.location.set(res);
    });
  }

  private getAdvisorDetail() {
    this.customerService.getAdvisorDetail().then((res) => {
      this.advisor.set(res);
    });
  }

  private async loadExternalContracts() {
    this.isLoading.set(true);
    try {
      const customerNo = this.userService.getCustomerNumber() ?? '';
      const data = await this.contractService.getExternalContracts(customerNo);

      const sortedData = [...data].sort((a, b) => {
        const dateA = a.endDate ? new Date(a.endDate).getTime() : 0;
        const dateB = b.endDate ? new Date(b.endDate).getTime() : 0;
        return dateA - dateB;
      });

      this.externalContracts.set(sortedData);
    } catch (error) {
      console.error('Failed to load external contracts', error);
      this.externalContracts.set([]);
    } finally {
      this.isLoading.set(false);
    }
  }
}
